import React, { useState } from "react";
import { X, Loader2, ArrowRight } from "lucide-react";
import { createLead } from "../lib/api";
import { toast } from "sonner";

export const LeadModal = ({ open, onClose, redirectAfter, navigate }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business: "",
    automation_needs: "",
  });
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.automation_needs.trim()) {
      toast.error("Fill in name, email, and what you want to automate.");
      return;
    }
    setLoading(true);
    try {
      await createLead({ ...form, source: "hero_modal" });
      toast.success("Welcome to the future. We'll be in touch within 24h.");
      setForm({ name: "", email: "", business: "", automation_needs: "" });
      onClose?.();
      if (redirectAfter && navigate) navigate(redirectAfter);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="lead-modal"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
        data-testid="lead-modal-overlay"
      />
      <div className="relative w-full max-w-lg glass-strong rounded-3xl p-8 shadow-[0_0_80px_rgba(0,240,255,0.18)]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-white"
          data-testid="lead-modal-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <p className="text-xs tracking-[0.2em] uppercase text-[#00F0FF] font-bold">
          Step 01 / Future Loading
        </p>
        <h3 className="font-display text-3xl font-black text-white mt-2 leading-tight">
          Tell us what to automate.
        </h3>
        <p className="text-zinc-400 text-sm mt-2">
          We reply with a tailored automation plan within 24 hours. No fluff.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400">
              Your name
            </label>
            <input
              data-testid="lead-input-name"
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Ada Lovelace"
              className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400">
              Work email
            </label>
            <input
              data-testid="lead-input-email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="ada@yourcompany.com"
              className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400">
              Business / Company
            </label>
            <input
              data-testid="lead-input-business"
              type="text"
              value={form.business}
              onChange={(e) => update("business", e.target.value)}
              placeholder="Optional"
              className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400">
              What do you want to automate?
            </label>
            <textarea
              data-testid="lead-input-needs"
              rows={4}
              value={form.automation_needs}
              onChange={(e) => update("automation_needs", e.target.value)}
              placeholder="e.g. Inbound lead routing, invoice OCR, AI customer support..."
              className="mt-1 w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition resize-none"
            />
          </div>

          <button
            type="submit"
            data-testid="lead-submit-btn"
            disabled={loading}
            className="w-full bg-[#00F0FF] text-black font-bold py-4 rounded-xl hover:bg-[#00C8E0] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                Take me to the future <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LeadModal;
